import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Form, FormField, SelectNew, TextField, PhoneField, Checkbox, Text, useMelioForm } from '@melio/penny';

import { useApp, Button, Banner, Icon, Illustration, MelioLogo } from '@/components/ds';
import { Spinner } from '@/components/code';
import { IndustryField, AddressFields } from '@/components/form';
import { getHealth, createEntity } from '@/api/payouts';
import { getSonarSessionToken, preloadSonarSdk } from '@/lib/sonarSdk';
import { trackEvent, useFlowAnalytics } from '@/lib/analytics';
import { isEnvironmentId, type EnvironmentId } from '@/config/environments';
import { SCHEDULE_URL } from '@/config/links';
import type { WithChildren } from '@/lib/types';

// welcome.tsx — new-customer onboarding. A partner requests API access, we generate a key and
// send them a link to /welcome. They paste the key (validated via GET /v2/health), then walk a
// multi-step KYB form that creates their first business entity via POST /v2/entities — the same
// call as the simulator's "Create a business" form, but broken into a guided, welcoming flow with
// full validation and address autocomplete (reused from components/form.tsx).
//
// Flow: connect (API key) → profile → business type & tax → address → people → review → done.

const emptyAddress = { _search: undefined, line1: '', line2: '', city: '', state: '', postalCode: '', aptNumber: '' };

type Step = 'connect' | 'profile' | 'tax' | 'address' | 'people' | 'review' | 'done';
type GateStatus = { kind: 'pending' | 'ok' | 'err'; msg: ReactNode } | null;

interface FieldSpec {
  name: string;
  label: string;
  test?: (v: any) => boolean;
  message?: string;
}

// businessType enum — matches CreateEntityRequest in payouts.api.external.yaml (no `ngo`).
const businessTypeOptions = [
  { value: 'sole-proprietorship', label: 'Sole Proprietorship', description: 'An unincorporated one-person business.' },
  { value: 'partnership', label: 'Partnership', description: 'Owned by two or more people (General, LP, LLP).' },
  { value: 'llc', label: 'Limited Liability Company (LLC)', description: 'A registered structure that shields owners from liability.' },
  { value: 'corporation', label: 'Corporation', description: 'A registered legal entity (C-Corp, S-Corp, Inc.).' },
  { value: 'trust', label: 'Trust', description: 'A fiduciary arrangement managed by a trustee.' },
  { value: 'non-profit', label: 'Non-Profit', description: 'A tax-exempt charitable, educational or public organization.' },
  { value: 'municipality', label: 'Municipality', description: 'A local government entity — a city, town or county.' },
];

// Allowed tax id types per formation type: `ein` for all; `ssn` only for sole-proprietorship/trust.
// `itin` is no longer accepted for any type (see TaxInfo in the spec), so it's never offered.
const taxTypesFor = (bt: string): string[] => (bt === 'sole-proprietorship' || bt === 'trust' ? ['ein', 'ssn'] : ['ein']);
const taxTypeLabels: Record<string, string> = { ein: 'EIN', ssn: 'SSN' };

// ---- validation helpers (mirror the schema patterns/limits) --------------------------------
const digitCount = (v: any) => (String(v ?? '').match(/\d/g) || []).length;
const TAX_RE = /^[0-9]{2}-?[0-9]{7}$|^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/;
const STATE_RE = /^[A-Za-z]{2}$/;
const ZIP_RE = /^\d{5}(-?\d{4})?$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const nameLen = (v: any) => typeof v === 'string' && v.trim().length >= 3 && v.trim().length <= 100;
const minLen2 = (v: any) => typeof v === 'string' && v.trim().length >= 2;
function validDob(v: any): boolean {
  if (!(v instanceof Date) || isNaN(v as unknown as number)) return false;
  const now = new Date();
  let age = now.getFullYear() - v.getFullYear();
  const m = now.getMonth() - v.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < v.getDate())) age--;
  return age >= 18 && age <= 120;
}

const profileSpecs: FieldSpec[] = [
  { name: 'name', label: 'Business (DBA) name', test: nameLen, message: 'Between 3 and 100 characters' },
  { name: 'legalName', label: 'Legal business name', test: nameLen, message: 'Between 3 and 100 characters' },
  { name: 'industry', label: 'Industry', test: (v) => !!(v && v.naicsCode), message: 'Select an industry' },
  { name: 'phoneNumber', label: 'Business phone', test: (v) => digitCount(v) >= 9, message: 'At least 9 digits' },
];
const taxSpecs: FieldSpec[] = [
  { name: 'businessType', label: 'Business type' },
  { name: 'taxType', label: 'Tax ID type' },
  { name: 'taxId', label: 'Tax ID', test: (v) => TAX_RE.test(String(v ?? '').trim()), message: 'A 9-digit EIN or SSN (e.g. 12-3456789)' },
];
const legalAddressSpecs: FieldSpec[] = [
  { name: 'legalAddress.line1', label: 'Legal address line 1' },
  { name: 'legalAddress.city', label: 'Legal city' },
  {
    name: 'legalAddress.state',
    label: 'Legal state',
    test: (v) => STATE_RE.test(String(v ?? '').trim()),
    message: 'Two-letter state code (e.g. NY)',
  },
  {
    name: 'legalAddress.postalCode',
    label: 'Legal postal code',
    test: (v) => ZIP_RE.test(String(v ?? '').trim()),
    message: '5 or 9 digit ZIP',
  },
];
const addressSpecs: FieldSpec[] = [
  { name: 'address.line1', label: 'Operating address line 1' },
  { name: 'address.city', label: 'Operating city' },
  {
    name: 'address.state',
    label: 'Operating state',
    test: (v) => STATE_RE.test(String(v ?? '').trim()),
    message: 'Two-letter state code (e.g. NY)',
  },
  {
    name: 'address.postalCode',
    label: 'Operating postal code',
    test: (v) => ZIP_RE.test(String(v ?? '').trim()),
    message: '5 or 9 digit ZIP',
  },
];
const peopleSpecs: FieldSpec[] = [
  { name: 'contactFirst', label: 'Contact first name', test: minLen2, message: 'At least 2 characters' },
  { name: 'contactLast', label: 'Contact last name', test: minLen2, message: 'At least 2 characters' },
  { name: 'ownerFirst', label: 'Owner first name', test: minLen2, message: 'At least 2 characters' },
  { name: 'ownerLast', label: 'Owner last name', test: minLen2, message: 'At least 2 characters' },
  { name: 'ownerEmail', label: 'Owner email', test: (v) => EMAIL_RE.test(String(v ?? '').trim()), message: 'Enter a valid email' },
  { name: 'dob', label: 'Owner date of birth', test: validDob, message: 'Owner must be between 18 and 120 years old' },
];

// A hidden ?env=<id> overrides key detection; otherwise the env slug is read from the key
// (same rule as the simulator gate).
function resolveEnv(key: string, fallback: EnvironmentId): EnvironmentId {
  const override = new URLSearchParams(window.location.search).get('env');
  if (override && isEnvironmentId(override)) return override;
  if (/_eilat_|eilat/i.test(key)) return 'eilat';
  if (/_staging01_|staging01/i.test(key)) return 'staging01';
  if (/_production_/i.test(key)) return 'production';
  return fallback;
}

// Normalize a phone to a leading-+ form when we can (US 10/11 digit); otherwise leave as typed.
function normPhone(v: any): string {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw.replace(/[^\d+]/g, '');
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return '+1' + d;
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  return raw;
}

const mkAddress = (a: any) => ({
  line1: a.line1,
  ...(a.line2 ? { line2: a.line2 } : {}),
  city: a.city,
  state: a.state,
  postalCode: a.postalCode,
  ...(a.aptNumber ? { aptNumber: a.aptNumber } : {}),
  countryCode: 'US',
});

// Compact horizontal sub-step indicator shown inside a step (e.g. the two address sub-steps).
function SubStepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <Fragment key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  background: done || active ? 'var(--accent)' : 'var(--neutral-300)',
                  color: done || active ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {done ? <Icon name="checked" size={13} color="#fff" /> : i + 1}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: done || active ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border-static)', margin: '0 12px' }} />}
          </Fragment>
        );
      })}
    </div>
  );
}

function SectionTitle({ children }: WithChildren) {
  return (
    <div style={{ gridColumn: '1 / -1', margin: '20px 0 4px' }}>
      <Text textStyle="heading3Semi">{children}</Text>
    </div>
  );
}

interface Milestone {
  label: string;
  sub: string;
  steps: Step[];
}
const MILESTONES: Milestone[] = [
  { label: 'Connect', sub: 'Paste your API key', steps: ['connect'] },
  { label: 'Business profile', sub: 'Name, industry & phone', steps: ['profile'] },
  { label: 'Business type & tax', sub: 'Formation & tax ID', steps: ['tax'] },
  { label: 'Addresses', sub: 'Legal & operating', steps: ['address'] },
  { label: 'Owner & contact', sub: 'Who runs the business', steps: ['people'] },
  { label: 'Review & create', sub: 'Confirm and submit', steps: ['review'] },
];

// Left brand panel with the vertical milestone rail (mirrors the /onboarding shell).
function MilestoneRail({ active, partnerName }: { active: number; partnerName?: string }) {
  return (
    <div
      style={{
        background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
        padding: '56px 48px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: -80,
          bottom: -80,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
        }}
      />
      <div style={{ position: 'relative', maxWidth: 360 }}>
        <h1 style={{ fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: '0 0 14px' }}>
          {partnerName ? `Welcome, ${partnerName}` : 'Welcome to Melio Payouts'}
        </h1>
        <p style={{ fontSize: 15, lineHeight: '23px', color: '#9aa3b2', margin: '0 0 40px' }}>
          This is the <strong style={{ color: '#fff' }}>Staging</strong> sandbox — a safe place to explore the Melio API with{' '}
          <strong style={{ color: '#fff' }}>no real money movement</strong>. Paste your Staging API key, then tell us about your business to
          create your first entity.
        </p>
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 24 }}>
          {MILESTONES.map((s, i) => {
            const done = active > i;
            const isActive = active === i;
            return (
              <div key={i} style={{ display: 'flex', gap: 14, opacity: done || isActive ? 1 : 0.5 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: done ? '#8fd6a8' : isActive ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                      border: isActive ? '2px solid var(--accent)' : 'none',
                    }}
                  >
                    {done ? (
                      <Icon name="checked" size={14} color="#16171f" />
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                    )}
                  </div>
                  {i < MILESTONES.length - 1 && (
                    <div
                      style={{ width: 2, flex: 1, minHeight: 22, background: done ? '#8fd6a8' : 'rgba(255,255,255,0.12)', margin: '3px 0' }}
                    />
                  )}
                </div>
                <div style={{ paddingBottom: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.label}</div>
                  <div style={{ fontSize: 12, color: '#9aa3b2', marginTop: 1 }}>{s.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '9px 0',
        borderBottom: '1px solid var(--border-static)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}

export function Welcome() {
  const app = useApp();
  useFlowAnalytics('welcome');

  const [step, setStep] = useState<Step>('connect');
  // Internal sub-step within the address step: 0 = legal registration, 1 = operating.
  const [addressSub, setAddressSub] = useState(0);

  // --- connection ----------------------------------------------------------
  // The key is only ever pasted here — never read from the URL, so it can't leak into
  // browser history, referrers or server logs. The environment is derived from the key.
  const [gateKey, setGateKey] = useState('');
  const [gateStatus, setGateStatus] = useState<GateStatus>(null);
  const [partnerName, setPartnerName] = useState<string>('');
  const envRef = useRef<EnvironmentId>(app.env);
  const apiKeyRef = useRef<string>('');

  // --- submission ----------------------------------------------------------
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  // Marks a step as attempted (Continue clicked) so change-driven re-validation kicks in.
  const stepAttemptedRef = useRef(false);

  const form: any = useMelioForm({
    defaultValues: {
      name: '',
      legalName: '',
      businessType: 'llc',
      phoneNumber: '',
      website: '',
      industry: undefined,
      taxType: 'ein',
      taxId: '',
      legalAddress: { ...emptyAddress },
      address: { ...emptyAddress },
      operatingSameAsLegal: true,
      contactFirst: '',
      contactLast: '',
      ownerFirst: '',
      ownerLast: '',
      ownerEmail: '',
      dob: null,
      ownerPhone: '',
    },
    onSubmit: () => {},
  });
  const { formProps, registerField, watch, getValues, setValue, setError, clearErrors } = form;

  const businessType = watch('businessType');
  const taxType = watch('taxType');
  const operatingSameAsLegal = watch('operatingSameAsLegal');
  const allowedTaxTypes = taxTypesFor(businessType);

  // Keep the tax id type valid for the chosen formation type.
  useEffect(() => {
    if (!allowedTaxTypes.includes(getValues().taxType)) setValue('taxType', 'ein');
  }, [businessType]);

  const goStep = (s: Step, sub = 0) => {
    trackEvent('welcome', 'Step Viewed', { step: s, subStep: sub });
    setStep(s);
    setAddressSub(sub);
    setSubmitErr('');
    stepAttemptedRef.current = false;
    window.scrollTo(0, 0);
  };
  const goAddressSub = (n: number) => {
    setAddressSub(n);
    setSubmitErr('');
    stepAttemptedRef.current = false;
    window.scrollTo(0, 0);
  };

  // The validation specs for the active step / sub-step.
  const currentSpecs: FieldSpec[] =
    step === 'profile'
      ? profileSpecs
      : step === 'tax'
        ? taxSpecs
        : step === 'address'
          ? addressSub === 0
            ? legalAddressSpecs
            : operatingSameAsLegal
              ? []
              : addressSpecs
          : step === 'people'
            ? peopleSpecs
            : [];

  // Subscribe to the form's values so a change to any field re-renders and updates the signature
  // below. `watch()` (value form) fires for every input type — text, select, date, address — unlike
  // the callback form, which some penny inputs don't reliably trigger.
  const allValues = watch();
  const getPath = (o: any, path: string) => path.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k]), o);
  // A signature built from the current step's field VALUES only (never errors), so re-validating —
  // which sets/clears errors — can't feed back into this and loop.
  const specSig = currentSpecs
    .map((s) => {
      const val = getPath(allValues, s.name);
      return `${s.name}=${val instanceof Date ? val.getTime() : (val ?? '')}`;
    })
    .join('|');

  // Once a step has been attempted (Continue clicked), re-validate on every field change so a
  // field's error clears the moment its value becomes valid.
  useEffect(() => {
    if (!stepAttemptedRef.current || !currentSpecs.length) return;
    currentSpecs.forEach((s) => clearErrors(s.name));
    let firstMsg = '';
    for (const s of currentSpecs) {
      const val = getPath(allValues, s.name);
      const ok = s.test ? s.test(val) : !(val == null || val === '');
      if (!ok) {
        setError(s.name, { type: 'required', message: s.message || 'Required' });
        if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
      }
    }
    setSubmitErr(firstMsg);
  }, [specSig]);

  const checkRequired = (specs: FieldSpec[]) => {
    specs.forEach((s) => clearErrors(s.name));
    const v = getValues();
    const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
    let firstMsg = '';
    for (const s of specs) {
      const val = get(s.name);
      const ok = s.test ? s.test(val) : !(val == null || val === '');
      if (!ok) {
        setError(s.name, { type: 'required', message: s.message || 'Required' });
        if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
      }
    }
    return firstMsg;
  };

  const advance = (specs: FieldSpec[], next: () => void) => {
    stepAttemptedRef.current = true;
    const err = checkRequired(specs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    setSubmitErr('');
    stepAttemptedRef.current = false;
    next();
  };

  // ---- connect ------------------------------------------------------------
  const connect = async () => {
    const key = gateKey.trim();
    if (!key) {
      setGateStatus({ kind: 'err', msg: 'Paste the API key we sent you.' });
      return;
    }
    const resolved = resolveEnv(key, app.env);
    envRef.current = resolved;
    apiKeyRef.current = key;
    setGateStatus({ kind: 'pending', msg: 'Connecting…' });
    try {
      const health = await getHealth({ env: resolved, apiKey: key });
      setPartnerName(health?.partnerName || '');
      app.setApiKey(key);
      app.setEnv(resolved);
      void preloadSonarSdk(resolved);
      trackEvent('welcome', 'Key Connected', { targetEnvironment: resolved });
      setGateStatus(null);
      goStep('profile');
    } catch (e: any) {
      const msg = e?.status === 401 ? 'That key wasn’t accepted. Double-check you copied it in full.' : e?.message || 'Could not connect.';
      trackEvent('welcome', 'Key Rejected', { status: e?.status, targetEnvironment: resolved });
      setGateStatus({ kind: 'err', msg });
    }
  };

  // ---- create the entity --------------------------------------------------
  const submit = async () => {
    setSubmitting(true);
    setSubmitErr('');
    try {
      const v = getValues();
      const legalAddr = mkAddress(v.legalAddress);
      const operatingAddr = v.operatingSameAsLegal ? legalAddr : mkAddress(v.address);
      const sonarToken = await getSonarSessionToken(envRef.current, apiKeyRef.current);
      const body = {
        type: 'business',
        externalId: 'partner-biz-' + crypto.randomUUID().slice(0, 8),
        name: v.name.trim(),
        legalName: v.legalName.trim(),
        phoneNumber: normPhone(v.phoneNumber),
        businessType: v.businessType,
        taxInfo: { type: v.taxType, identifier: String(v.taxId).trim() },
        industry: { naicsCode: v.industry?.naicsCode, ...(v.industry?.name ? { name: v.industry.name } : {}) },
        contact: { firstName: v.contactFirst.trim(), lastName: v.contactLast.trim() },
        ...(v.website ? { website: v.website.trim() } : {}),
        address: operatingAddr,
        legalAddress: legalAddr,
        owner: {
          firstName: v.ownerFirst.trim(),
          lastName: v.ownerLast.trim(),
          email: v.ownerEmail.trim(),
          ...(v.dob instanceof Date ? { dateOfBirth: v.dob.toISOString().split('T')[0] } : {}),
          ...(v.ownerPhone ? { phoneNumber: normPhone(v.ownerPhone) } : {}),
        },
      };
      await createEntity({ env: envRef.current, apiKey: apiKeyRef.current }, body, { sonarToken });
      trackEvent('welcome', 'Business Created', { businessType: v.businessType });
      goStep('done');
    } catch (ex: any) {
      trackEvent('welcome', 'Business Creation Failed', { businessType: v.businessType, reason: ex?.message });
      setSubmitErr(ex?.message || 'We couldn’t create the business. Check the details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeMilestone = step === 'done' ? MILESTONES.length : MILESTONES.findIndex((m) => m.steps.includes(step));

  // Enter advances the current step (except while a listbox/combobox is open).
  const makeKeyDown = (advanceFn: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (target.closest('[role="listbox"]') || target.closest('[role="option"]') || target.closest('[role="combobox"]')) return;
    e.preventDefault();
    advanceFn();
  };

  const v = getValues();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div
        style={{
          height: 64,
          borderBottom: '1px solid var(--border-static)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 28px',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MelioLogo height={22} />
          <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>API onboarding</span>
        </div>
        <div style={{ flex: 1 }} />
      </div>

      <div className="grid-2" style={{ flex: 1, gridTemplateColumns: '0.85fr 1.15fr', gap: 0, alignItems: 'stretch' }}>
        <MilestoneRail active={activeMilestone} partnerName={partnerName} />

        {/* Right form panel */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '56px 48px', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 460 }}>
            {/* ── Connect ── */}
            {step === 'connect' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(connect)}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Let's get you set up</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  Welcome! You've been given a Staging API key to explore the Melio API. Paste it below to begin — we'll walk you through
                  creating your business in a few short steps. This is a sandbox, so no real money moves.
                </p>
                <div style={{ marginBottom: 4, fontSize: 14, fontWeight: 600 }}>API key for Staging</div>
                <TextField
                  type="password"
                  autoComplete="off"
                  placeholder="melio_secret_staging01_…"
                  value={gateKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGateKey(e.target.value)}
                />
                {gateStatus && gateStatus.kind !== 'pending' && (
                  <Banner kind={gateStatus.kind === 'err' ? 'critical' : 'success'} style={{ marginTop: 16 }}>
                    {gateStatus.msg}
                  </Banner>
                )}
                <Button
                  fullWidth
                  size="large"
                  style={{ marginTop: 22 }}
                  rightIcon={gateStatus?.kind === 'pending' ? undefined : 'arrow-right'}
                  disabled={gateStatus?.kind === 'pending'}
                  onClick={connect}
                >
                  {gateStatus?.kind === 'pending' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Spinner size={16} color="#fff" /> Connecting…
                    </span>
                  ) : (
                    'Connect & continue'
                  )}
                </Button>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    marginTop: 18,
                    fontSize: 12.5,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  <Icon name="lock" size={15} color="var(--text-secondary)" />
                  <span>Your key is held in memory for this session only — it's never stored, and only used to call the Melio API.</span>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '14px 0 0' }}>
                  By continuing, you accept Melio's{' '}
                  <a
                    href="https://meliopayments.com/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--accent-text)' }}
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://developers.melio.com/api-agreement"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--accent-text)' }}
                  >
                    API Agreement
                  </a>
                  . Payment services are subject to Melio's{' '}
                  <a
                    href="https://meliopayments.com/terms-of-service/"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--accent-text)' }}
                  >
                    Terms of Service
                  </a>
                  .
                </p>
              </div>
            )}

            {/* ── Business profile ── */}
            {step === 'profile' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(() => advance(profileSpecs, () => goStep('tax')))}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Tell us about your business</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  The business that will send payments. This is the legal entity we onboard on Melio.
                </p>
                <Form {...formProps} columns={2}>
                  <FormField
                    isRequired
                    {...registerField('name')}
                    labelProps={{ label: 'Business (DBA) name *', tooltipProps: { content: 'How your business is known day to day.' } }}
                    render={(props: any) => <TextField {...props} placeholder="Acme Supplies" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('legalName')}
                    labelProps={{ label: 'Legal business name *', tooltipProps: { content: 'As it appears on your tax return.' } }}
                    render={(props: any) => <TextField {...props} placeholder="Acme Supplies LLC" />}
                  />
                  <IndustryField
                    form={form}
                    env={envRef.current}
                    apiKey={apiKeyRef.current}
                    name="industry"
                    label="Business industry"
                    isRequired
                    colSpan={2}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('phoneNumber')}
                    labelProps={{ label: 'Business phone number *' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                  <FormField
                    colSpan={2}
                    showOptionalIndicator
                    {...registerField('website')}
                    labelProps={{ label: 'Website' }}
                    render={(props: any) => <TextField {...props} placeholder="https://acmesupplies.com" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('connect')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={() => advance(profileSpecs, () => goStep('tax'))}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Business type & tax ── */}
            {step === 'tax' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(() => advance(taxSpecs, () => goStep('address')))}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Business type & tax</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  How the business is formed and how it files. The allowed tax ID type depends on the business type.
                </p>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('businessType')}
                    labelProps={{ label: 'Business type *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={businessTypeOptions}
                        optionRenderer={(option: any) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                            <Text textStyle="body3Semi">{option.label}</Text>
                            {option.description && (
                              <Text textStyle="body4" color="global.neutral.700">
                                {option.description}
                              </Text>
                            )}
                          </div>
                        )}
                        placeholder="Select a type"
                      />
                    )}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxType')}
                    labelProps={{ label: 'Tax ID type *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={allowedTaxTypes.map((t) => ({ value: t, label: taxTypeLabels[t] }))}
                        placeholder="Select"
                      />
                    )}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxId')}
                    labelProps={{
                      label: `${taxTypeLabels[taxType] || 'Tax ID'} *`,
                      tooltipProps: { content: '9-digit tax identifier, optionally dash-separated (e.g. 12-3456789).' },
                    }}
                    render={(props: any) => <TextField {...props} placeholder={taxType === 'ssn' ? '123-45-6789' : '12-3456789'} />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('profile')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={() => advance(taxSpecs, () => goStep('address'))}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Addresses ── */}
            {step === 'address' && (
              <div style={{ animation: 'om-fade-up .3s ease' }}>
                <SubStepper steps={['Legal', 'Operating']} current={addressSub} />

                {/* Sub 0: legal registration address */}
                {addressSub === 0 && (
                  <>
                    <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Legal registration address</h2>
                    <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                      The address the business is registered at. Start typing to autocomplete.
                    </p>
                    <Form {...formProps} columns={2}>
                      <AddressFields
                        form={form}
                        env={envRef.current}
                        apiKey={apiKeyRef.current}
                        parentFieldName="legalAddress"
                        label="Legal address *"
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('tax')}>
                        Back
                      </Button>
                      <Button
                        fullWidth
                        size="large"
                        rightIcon="arrow-right"
                        onClick={() => advance(legalAddressSpecs, () => goAddressSub(1))}
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {/* Sub 1: operating address */}
                {addressSub === 1 && (
                  <>
                    <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Operating address</h2>
                    <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                      Where the business operates day to day. Start typing to autocomplete.
                    </p>
                    <Form {...formProps} columns={2}>
                      <FormField
                        colSpan={2}
                        labelProps={{ label: 'Operating address', isHidden: true }}
                        render={() => (
                          <Checkbox
                            value={operatingSameAsLegal}
                            onChange={(checked: boolean) => setValue('operatingSameAsLegal', checked)}
                            label="Same as legal registration address"
                          />
                        )}
                      />
                      {!operatingSameAsLegal && (
                        <AddressFields
                          form={form}
                          env={envRef.current}
                          apiKey={apiKeyRef.current}
                          parentFieldName="address"
                          label="Operating address"
                        />
                      )}
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goAddressSub(0)}>
                        Back
                      </Button>
                      <Button
                        fullWidth
                        size="large"
                        rightIcon="arrow-right"
                        onClick={() => advance(operatingSameAsLegal ? [] : addressSpecs, () => goStep('people'))}
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Owner & contact ── */}
            {step === 'people' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(() => advance(peopleSpecs, () => goStep('review')))}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Owner & contact</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  The person who owns the business and a point of contact.
                </p>
                <Form {...formProps} columns={2}>
                  <SectionTitle>Business owner</SectionTitle>
                  <FormField
                    isRequired
                    {...registerField('ownerFirst')}
                    labelProps={{ label: 'First name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Jane" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('ownerLast')}
                    labelProps={{ label: 'Last name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Doe" />}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerEmail')}
                    labelProps={{ label: 'Email *' }}
                    render={(props: any) => <TextField {...props} type="email" placeholder="jane.doe@acmesupplies.com" />}
                  />
                  <Form.DateField
                    {...registerField('dob')}
                    isRequired
                    labelProps={{ label: 'Date of birth' }}
                    isTypable
                    format="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                  />
                  <FormField
                    showOptionalIndicator
                    {...registerField('ownerPhone')}
                    labelProps={{ label: 'Phone' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                  <SectionTitle>Point of contact</SectionTitle>
                  <FormField
                    isRequired
                    {...registerField('contactFirst')}
                    labelProps={{ label: 'First name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Jane" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('contactLast')}
                    labelProps={{ label: 'Last name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Doe" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('address', 1)}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={() => advance(peopleSpecs, () => goStep('review'))}>
                    Review
                  </Button>
                </div>
              </div>
            )}

            {/* ── Review & create ── */}
            {step === 'review' && (
              <div style={{ animation: 'om-fade-up .3s ease' }}>
                <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>Review & create</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 24px' }}>One last look before you submit.</p>
                <div style={{ border: '1px solid var(--border-static)', borderRadius: 12, padding: '4px 18px 14px' }}>
                  <SummaryRow label="Business (DBA)" value={v.name} />
                  <SummaryRow label="Legal name" value={v.legalName} />
                  <SummaryRow
                    label="Business type"
                    value={businessTypeOptions.find((o) => o.value === v.businessType)?.label || v.businessType}
                  />
                  <SummaryRow label="Industry" value={v.industry?.name || v.industry?.naicsCode} />
                  <SummaryRow label="Phone" value={normPhone(v.phoneNumber)} />
                  {v.website ? <SummaryRow label="Website" value={v.website} /> : null}
                  <SummaryRow label="Tax ID" value={`${taxTypeLabels[v.taxType] || v.taxType} · ${v.taxId}`} />
                  <SummaryRow
                    label="Legal address"
                    value={[v.legalAddress.line1, v.legalAddress.city, v.legalAddress.state, v.legalAddress.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <SummaryRow
                    label="Operating address"
                    value={
                      v.operatingSameAsLegal
                        ? 'Same as legal'
                        : [v.address.line1, v.address.city, v.address.state, v.address.postalCode].filter(Boolean).join(', ')
                    }
                  />
                  <SummaryRow label="Owner" value={`${v.ownerFirst} ${v.ownerLast} · ${v.ownerEmail}`} />
                  <SummaryRow label="Contact" value={`${v.contactFirst} ${v.contactLast}`} />
                </div>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" disabled={submitting} onClick={() => goStep('people')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon={submitting ? undefined : 'checked'} disabled={submitting} onClick={submit}>
                    {submitting ? <Spinner size={16} color="#fff" /> : 'Submit'}
                  </Button>
                </div>
              </div>
            )}

            {/* ── Done ── */}
            {step === 'done' && (
              <div style={{ animation: 'om-fade-up .3s ease', textAlign: 'center' }}>
                <Illustration name="celebration" size={130} style={{ margin: '0 auto 8px' }} />
                <h2 style={{ margin: 0, fontSize: 26, letterSpacing: '-0.01em' }}>You're all set{partnerName ? `, ${partnerName}` : ''}</h2>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px auto 12px', maxWidth: 420 }}>
                  Thanks for moving forward in the Melio API Beta Program.
                </p>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '0 auto 24px', maxWidth: 440 }}>
                  While we review your details, start building in the staging environment. Explore the API and test your integration in the
                  simulator. We'll follow up if we have questions, and let you know as soon as you're set for production.
                </p>
                <Button
                  fullWidth
                  size="large"
                  rightIcon="arrow-right"
                  onClick={() => window.open(SCHEDULE_URL, '_blank', 'noopener,noreferrer')}
                >
                  Schedule a call
                </Button>
                <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                  <Button
                    fullWidth
                    variant="tertiary"
                    size="large"
                    onClick={() => window.open('/simulator', '_blank', 'noopener,noreferrer')}
                  >
                    Open the simulator
                  </Button>
                  <Button
                    fullWidth
                    variant="tertiary"
                    size="large"
                    onClick={() => window.open('/reference', '_blank', 'noopener,noreferrer')}
                  >
                    View the docs
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
